<template>
  <Modal :open="isOpen" size="medium" @close="closeModal">
    <h1 class="text-gray-900 font-bold mb-4">Modifier le produit</h1>

    <VeeForm v-slot="{ handleSubmit }" :validation-schema="schema" as="div">
      <form class="space-y-4" @submit="handleSubmit($event, handleUpdateProductItem)" ref="UpdateItemForm">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
          <div>
            <label for="itemTitle" class="block text-sm text-gray-700">
              Nom du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemTitle"
                name="itemTitle"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Nom du produit"
                v-model="itemTitle"
              />
              <ErrorMessage name="itemTitle" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPrice" class="block text-sm text-gray-700">
              Prix du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                id="itemPrice"
                name="itemPrice"
                type="text"
                class="pr-16 shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="249,99"
                v-model="itemPriceFormatted"
              />
              <div class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                <span class="text-gray-500 sm:text-sm" id="price-currency">EUR</span>
              </div>
              <ErrorMessage name="itemPrice" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemType" class="block text-sm text-gray-700">
              Type du produit
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                as="select"
                id="itemType"
                name="itemType"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemType"
              >
                <option disabled value="">Veuillez sélectionner un type</option>
                <option v-for="type in itemTypes(plan)" :key="type" :value="type">{{ $t(type) }}</option>
              </Field>
              <ErrorMessage name="itemType" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemReferenceId" class="block text-sm text-gray-700">
              Numéro de série ou IMEI
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemReferenceId"
                name="itemReferenceId"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="35478405412998702"
                v-model="itemReferenceId"
              />
              <ErrorMessage name="itemReferenceId" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemVendor" class="block text-sm text-gray-700">
              Vendeur / Marque
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemVendor"
                name="itemVendor"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Samsung, Apple, Sony..."
                v-model="itemVendor"
              />
              <ErrorMessage name="itemVendor" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemModel" class="block text-sm text-gray-700">
              Modèle
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2">
              <Field
                id="itemModel"
                name="itemModel"
                type="text"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                placeholder="Ex : Series 6, Q80T, Hero9, PlayStation 5..."
                v-model="itemModel"
              />
              <ErrorMessage name="itemModel" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
          <div>
            <label for="itemPurchaseDate" class="block text-sm text-gray-700">
              Date d'achat
              <span class="ml-1 text-red-600">*</span>
            </label>
            <div class="mt-2 relative">
              <Field
                type="date"
                name="itemPurchaseDate"
                id="itemPurchaseDate"
                class="shadow-xs focus:ring-primary focus:border-primary block w-full sm:text-sm border-gray-300 rounded-md"
                v-model="itemPurchaseDate"
              />
              <ErrorMessage name="itemPurchaseDate" class="mt-2 text-sm text-red-600" />
            </div>
          </div>
        </div>
        <div class="mt-6">
          <div class="flex flex-row gap-4">
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent px-4 py-2 text-lg font-medium transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden text-button-font-primary bg-primary uppercase"
                type="submit"
              >
                {{ $t("Modifier") }}
              </button>
            </div>
            <div class="sm:w-1/2">
              <button
                class="w-full border border-transparent bg-red-100 px-4 py-2 text-lg font-medium text-red-700 transition duration-300 transform hover:scale-105 rounded-lg focus:outline-hidden"
                type="button"
                @click="closeModal"
              >
                {{ $t("Close") }}
              </button>
            </div>
          </div>
        </div>
      </form>
    </VeeForm>
  </Modal>
</template>

<script>
import { Modal } from "@estaly/ui";
import PlanSalesService from "@/api/services/planSales";
import { mapActions, mapGetters } from "vuex";
import { ErrorMessage, Field, Form as VeeForm } from "vee-validate";
import * as yup from "yup";

export default {
  components: {
    Modal,
    ErrorMessage,
    Field,
    VeeForm,
  },

  props: ["isOpen", "plan", "productItem"],

  data: () => {
    const schema = yup.object({
      itemTitle: yup.string().required("Le nom du produit est requis"),
      itemReferenceId: yup.string().required("Le numéro de série est requis"),
      itemVendor: yup.string().required("La marque du produit est requise"),
      itemModel: yup.string().required("Le modèle du produit est requis"),
      itemPurchaseDate: yup
        .date()
        .nullable()
        .transform((value, originalValue) => (originalValue === "" ? null : value))
        .max(new Date(), "La date d'achat ne peut pas être dans le futur")
        .min(
          new Date(new Date().setFullYear(new Date().getFullYear() - 3)),
          "La date d'achat ne peut pas être supérieure à 3 ans",
        )
        .required("La date d'achat est requise")
        .typeError("La date d'achat est invalide"),
      itemPrice: yup
        .string()
        .required("Le prix du produit est requis.")
        .matches(/^\d+(\.\d{1,2})?$|^\d+(,\d{1,2})?$/, "Le format du prix est invalide."),
      itemType: yup.string().required("Le type du produit est requis"),
    });
    return {
      schema,
      updateSuccess: false,
      itemTitle: "",
      itemPrice: "",
      itemType: "",
      itemReferenceId: "",
      itemVendor: "",
      itemModel: "",
      itemPurchaseDate: "",
    };
  },

  computed: {
    ...mapGetters("planSales", ["itemTypes"]),
    itemPriceFormatted: {
      get() {
        return this.itemPrice === undefined ? "" : this.itemPrice.toString().replace(".", ",");
      },
      set(value) {
        this.itemPrice = value.replace(",", ".");
      },
    },
  },

  methods: {
    ...mapActions("notifications", ["notify"]),

    closeModal() {
      this.$emit("closeUpdateProductItemModal", this.updateSuccess);
    },

    refreshPlanSale() {
      this.$emit("refreshPlanSale");
    },

    async handleUpdateProductItem() {
      const formData = new FormData();
      formData.append("title", this.itemTitle);
      formData.append("price", parseFloat(this.itemPrice.replace(",", ".")));
      formData.append("item_type", this.itemType);
      formData.append("reference_id", this.itemReferenceId);
      formData.append("vendor", this.itemVendor);
      formData.append("model", this.itemModel);
      formData.append("purchase_date", this.itemPurchaseDate);
      formData.append("item_image", this.itemImage);

      const { success } = await PlanSalesService.updateProductItem(this.plan.id, this.productItem.id, formData);
      if (!success) return;

      this.notify({
        category: "simple",
        type: "success",
        title: "Le produit a été modifié",
      });
      this.updateSuccess = true;
      this.closeModal();
      this.refreshPlanSale();
    },
  },

  watch: {
    productItem: {
      deep: true,
      handler(newValue) {
        if (newValue) {
          this.itemTitle = newValue.title || "";
          this.itemPrice = newValue.price.toString().replace(".", ",") || "";
          this.itemType = newValue.itemType || "";
          this.itemReferenceId = newValue.referenceId || "";
          this.itemVendor = newValue.vendor || "";
          this.itemModel = newValue.model || "";
          if (newValue.purchaseDate) {
            const parts = newValue.purchaseDate.split("/");
            if (parts.length === 3) {
              this.itemPurchaseDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
            } else {
              this.itemPurchaseDate = "";
            }
          } else {
            this.itemPurchaseDate = "";
          }
        }
      },
      immediate: true,
    },
  },
};
</script>
